import React from 'react';
import { bannerSurface } from '../theme';
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types';
import ReactDOM from "react-dom";
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import { SportsRugbySharp } from '@mui/icons-material';
import { red } from '@mui/material/colors';

const PREFIX = 'CollapsableSection';

// every section whose notice is currently fixed to the bottom: with two sections warning at once
// (languages and theme settings, say) the bars would sit on top of each other, so they stack
// instead - the section lower on the page keeps the bottom, the ones above it move up
const fixedNotices = new Set();
const FIXED_NOTICE_HEIGHT = 43;
const refreshFixedNotices = () => fixedNotices.forEach(section => section.forceUpdate());
// the section currently opened over the backdrop; other sections' pinned bars hide meanwhile,
// a bar for a section behind the backdrop only distracts
let backdropSection = null;
const classes = {
  root: `${PREFIX}-root`,
  backdrop: `${PREFIX}-backdrop`,
  paper: `${PREFIX}-paper`,
  collapseToggler: `${PREFIX}-collapseToggler`,
  toolbarToggler: `${PREFIX}-toolbarToggler`,
  toolbarActions: `${PREFIX}-toolbarActions`,
  icon: `${PREFIX}-icon`,
  contentContainer: `${PREFIX}-contentContainer`,
  noticed: `${PREFIX}-noticed`,
  card: `${PREFIX}-card`,
  media: `${PREFIX}-media`,
  actions: `${PREFIX}-actions`,
  title: `${PREFIX}-title`,
  expand: `${PREFIX}-expand`,
  expandOpen: `${PREFIX}-expandOpen`,
  avatar: `${PREFIX}-avatar`,
  cardActions: `${PREFIX}-cardActions`,
  cardHeader: `${PREFIX}-cardHeader`,
  button: `${PREFIX}-button`,
  notice: `${PREFIX}-notice`,
  error: `${PREFIX}-error`,
  info: `${PREFIX}-info`,
  sidebarLogo: `${PREFIX}-sidebarLogo`,
  noticeIcon: `${PREFIX}-noticeIcon`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    '&.pinned .collapsable-section': {
      position: 'relative',
      zIndex: 1000,
    }
  },
  [`& .${classes.backdrop}`]: {
    position: 'fixed',
    zIndex: 900,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, .25)',
    cursor: 'pointer',
  },
  [`& .${classes.paper}`]: {
    width: '100%',
    margin: 0,
    marginBottom: theme.spacing(3),
    overflow: 'hidden',
  },
  [`& .${classes.toolbarToggler}`]: {
    cursor: 'pointer',
  },
  [`& .${classes.collapseToggler}`]: {
    cursor: 'pointer',
    color: `${theme.palette.text.primary} !important`,
  },
  [`& .${classes.icon}`]: {
    marginRight: theme.spacing(2),
    opacity: .5,
  },
  [`& .${classes.contentContainer}`]: {
    //marginBottom: theme.spacing(2),
  },
  [`& .${classes.noticed}`]: {
    borderRadius: '0 0 4px 4px',
  },
  [`& .${classes.card}`]: {
    //maxWidth: 400,
  },
  [`& .${classes.media}`]: {
    width: '100%',
    paddingTop: '56.25%', // 16:9
    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ccc',
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
  },
  [`& .${classes.title}`]: {
    fontSize: 16,
  },
  [`& .${classes.expand}`]: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    marginLeft: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginRight: -8,
    },
  },
  [`& .${classes.expandOpen}`]: {
    transform: 'rotate(180deg)',
  },
  [`& .${classes.avatar}`]: {
    backgroundColor: red[500],
  },
  [`& .${classes.cardActions}`]: {
    padding: '0px 12px 14px 12px'
  },
  [`& .${classes.cardHeader}`]: {
    alignItems: 'baseline',
    paddingBottom: 0
  },
  [`& .${classes.button}`]: {
    marginLeft: 10,
  },
  [`& .${classes.notice}`]: {
    marginBottom: 0,
    borderRadius: '4px 4px 0 0',
    overflow: 'hidden',
    border: `solid 1px ${theme.palette.divider}`,
    '& .toolbar': {
      minHeight: 'auto !important',
      padding: '2px 16px',
    },
    // the toolbar is a flex row, without this the text only spans its own width
    '& p': {
      margin: '8px !important',
      flex: 1,
    },
    '& svg': {
      fontSize: 24,
      marginTop: -2,
      marginRight: 8,
    },
    '&.is-fixed': {
      position: 'fixed',
      bottom: 0,
      zIndex: 9,
    }
  },
  [`& .${classes.error}`]: bannerSurface(theme, theme.palette.errorBar),
  [`& .${classes.info}`]: bannerSurface(theme, theme.palette.accentSurface),
  [`& .${classes.sidebarLogo}`]: {
    position: 'absolute',
    left: 0,
    top: 15,
    width: 240,
    textAlign: 'center',
  },
}));


class CollapsableSection extends React.Component {
  intersectionObs = null;
  targetIsIntersecting = null;
  backdropDelay = null;
  tempScroll = 0;

  constructor(props) {
    super(props);

    this.state = {
      open: false,
      fixedNotice: false,
      showBackdrop: false,
    }
  }

  componentDidMount() {
    if(this.props.deeplink) {
      if(window.location.hash === `#${this.props.deeplink}`) {
        if(!this.props.initialOpen && !this.props.pending) {
          this.handleToggleOpen()
        }
        if(!this.props.initialOpen && this.props.pending) {
          this.openWhenLoaded = true;
          setTimeout(this.scrollToTop, 100);
        }
      }
    }
    if(this.props.noticeShow === true) {
      this.observeSection();
    }
    const mainFrame = document.getElementById('mainFrame');
    if (mainFrame && typeof ResizeObserver !== 'undefined') {
      this._mainFrameResizeObs = new ResizeObserver(() => {
        if (this.state.fixedNotice) this.forceUpdate();
      });
      this._mainFrameResizeObs.observe(mainFrame);
    }
  }

  componentDidUpdate() {
    this.syncFixedNoticeStack();
    this.syncBackdropOwner();
  }

  // owner from the moment a pinned section opens, not from the backdrop's delayed fade-in:
  // in between, the other sections scroll out of view and their bars would flash at the bottom
  syncBackdropOwner = () => {
    // the owner keeps its role through the close animation (closingGrace) so the other bars
    // come back once the page has settled, not while it is still moving
    const owner = (this.state.open || this.closingGrace === true) && this.props.pinSectionOnOpen;
    if(owner && backdropSection !== this) {
      backdropSection = this;
      refreshFixedNotices();
    } else if(!owner && backdropSection === this) {
      backdropSection = null;
      refreshFixedNotices();
    }
  }

  // pins the notice while the section is scrolled out of view. Also needed on mount: a section
  // that renders with its notice already showing (the languages bar, from store data) never saw
  // the hidden-to-shown transition and stayed inline forever
  observeSection = () => {
    const node = this.rootRef ? this.rootRef.getElementsByClassName('collapsable-section') : [];
    if(!node[0] || this.intersectionObs) return;
    this.intersectionObs = new IntersectionObserver(this.intersectionCallback, {
      rootMargin: '0px',
      threshold: [0]
    });
    this.intersectionObs.observe(node[0]);
  }

  syncFixedNoticeStack = () => {
    const fixed = this.state.fixedNotice === true && this.props.noticeShow === true && !this.state.open;
    if(fixed && !fixedNotices.has(this)) {
      fixedNotices.add(this);
      refreshFixedNotices();
    } else if(!fixed && fixedNotices.has(this)) {
      fixedNotices.delete(this);
      refreshFixedNotices();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.closingGraceTimer);
    if(fixedNotices.has(this)) {
      fixedNotices.delete(this);
      refreshFixedNotices();
    }
    if(backdropSection === this) {
      backdropSection = null;
      refreshFixedNotices();
    }
    if(this.intersectionObs) this.intersectionObs.disconnect();
    window.removeEventListener('resize', this.onResize);
    if(window.mainFrameRef) window.mainFrameRef.removeEventListener('scroll', this._noScroll);
    if(this._mainFrameResizeObs) this._mainFrameResizeObs.disconnect();
  }

  componentWillReceiveProps(newProps, newState) {
    if(this.props.pending && newProps.pending === false && this.openWhenLoaded) {
      this.openWhenLoaded = false;
      this.handleToggleOpen()
    }
    //if(newProps.noticeType === 'error') {
      if(this.props.noticeShow === false && newProps.noticeShow === true) {
        this.observeSection();
      }
      if(this.props.noticeShow === true && newProps.noticeShow === false) {
        if(this.intersectionObs) {
          this.intersectionObs.disconnect();
          this.intersectionObs = null;
        }
        this.setState({ fixedNotice: false });
      }
    //}
    if(this.props.noticeShow === true && newProps.noticeShow === false) {
      if(this.hasUserInteracted) {
        setTimeout(() => this.scrollToTop(true), 1);
      }
    }
    if(this.props.scrollToTop !== newProps.scrollToTop) {
      setTimeout(() => this.scrollToTop(true), 1);
    }
  }

  intersectionCallback = (intersections) => {
    intersections.forEach((intersection) => {
      //console.log(intersection)

      if(this.targetIsIntersecting === null) {
        this.targetIsIntersecting = intersection.isIntersecting ? true : false;
      }
      if(this.targetIsIntersecting === false && intersection.isIntersecting === true) {
        this.targetIsIntersecting = true;
        this.setState({ fixedNotice: false });
      }
      else if(this.targetIsIntersecting === true && intersection.isIntersecting === false ||
              this.targetIsIntersecting === false && intersection.isIntersecting === false) {
        this.targetIsIntersecting = false;
        this.setState({ fixedNotice: true });
      }
    });
  }

  getFixedNoticeStyle = () => {
    if (!this.rootRef) return { left: 8, right: 8 };
    const rect = this.rootRef.getBoundingClientRect();
    // one slot per fixed notice of a section further down the page
    // stacked by the bars' real heights: a wrapped notice text is taller than the nominal bar
    const bottom = Array.from(fixedNotices)
      .filter(section => section !== this && section.rootRef && section.rootRef.getBoundingClientRect().top > rect.top)
      .reduce((sum, section) => sum + (section.noticeRef?.offsetHeight || FIXED_NOTICE_HEIGHT), 0);
    return {
      left: rect.left,
      width: rect.width,
      bottom,
      ...(backdropSection && backdropSection !== this ? { display: 'none' } : {}),
    };
  }

  onResize = () => {
    this.scrollToTop(true);
  }

  // closing: the bar must not pin from the observer's stale state while the content collapses
  // and the page scrolls back - reset, re-observe, and hold the owner role for the animation
  beginClosingGrace = () => {
    if(!this.props.pinSectionOnOpen) return;
    this.closingGrace = true;
    this.targetIsIntersecting = null;
    if(this.intersectionObs) {
      this.intersectionObs.disconnect();
      this.intersectionObs = null;
    }
    this.setState({ fixedNotice: false }, () => {
      if(this.props.noticeShow === true) this.observeSection();
    });
    clearTimeout(this.closingGraceTimer);
    this.closingGraceTimer = setTimeout(() => {
      this.closingGrace = false;
      this.forceUpdate();
    }, 700);
  }

  // the whole bar toggles; its own buttons, and clicks bubbling in from portals such as
  // the upgrade hint's tooltip, keep their own action. A disabled button lets the click
  // through to its wrapper, so the whole button area is excluded
  handleToolbarClick = (e) => {
    if(!e.currentTarget.contains(e.target)) return;
    if(e.target.closest(`button, a, .${classes.toolbarActions}`)) return;
    this.handleToggleOpen();
  }

  handleToggleOpen = () => {
    this.hasUserInteracted = true;
    window.location.hash = !this.state.open ? `#${this.props.deeplink}` : '';
    if(this.state.open) this.beginClosingGrace();
    this.setState({ open: !this.state.open }, () => {
      if(this.state.open) {
        if(this.props.pinSectionOnOpen) {
          this.blockScroll();
          this.backdropDelay = setTimeout(() => {
            this.setState({ showBackdrop: true });
          }, 500);
        }
        if(this.props.onOpened) {
          this.props.onOpened();
        }
      } else {
        this.allowScroll();
        if(this.backdropDelay) {
          clearTimeout(this.backdropDelay)
        }
        this.setState({ showBackdrop: false });
        if(this.props.onClosed) {
          this.props.onClosed();
        }
      }
    });
  }

  handleClose = () => {
    this.allowScroll();
    if (this.props.deeplink) window.location.hash = '';
    this.beginClosingGrace();
    this.setState({ open: false, showBackdrop: false });
  }

  handleClosed = () => {
    if(this.props.onClosed) {
      this.props.onClosed();
    }
  }

  handleClickNotice = () => {
    window.location.hash = !this.state.open ? `#${this.props.deeplink}` : '';
    this.setState({ open: true });
    this.scrollToTop();
  }

  handleCtaAction = () => {

  }

  _noScroll = () => {
    //window.mainFrameRef.scrollTo(0, this.tempScroll);
    this.scrollToTop(true);
  }
  blockScroll = () => {
    setTimeout(() => {
      //window.mainFrameRef.addEventListener('scroll', this._noScroll);
      if(window.mainFrameRef) {
        window.mainFrameRef.style.setProperty('overflow', 'auto', 'important');
        this.scrollToTop(true);
        setTimeout(() => {
          window.mainFrameRef.style.setProperty('overflow', 'hidden', 'important');
        }, 10);
      }
      window.addEventListener('resize', this.onResize);
    }, 750);
  }
  allowScroll = () => {
    //window.mainFrameRef.removeEventListener('scroll', this._noScroll);
    window.mainFrameRef.style.setProperty('overflow', 'auto', 'important');
    window.removeEventListener('resize', this.onResize);
  }

  setRootRef = (ref) => {
    this.rootRef = ref;
    if(this.rootRef) {
      this.boundings = this.rootRef.getBoundingClientRect();
    }
  }

  scroll = null;
  scrollToTop = (jump = false) => {
    // header height to clear; the root's top already includes the notice bar, so an opened
    // section shows its bar right under the header instead of hiding it behind
    let offset = (this.props.pinSectionOnOpen && this.state.open) ? 87 : 63;
    if(this.rootRef) {
      this.boundings = this.rootRef.getBoundingClientRect();
      if(window.mainFrameRef) {
        this.scroll = window.mainFrameRef.scrollTop;
        if(jump) {
          window.mainFrameRef.scroll({top: this.boundings.top + this.scroll - offset });
        } else {
          window.mainFrameRef.scroll({top: this.boundings.top + this.scroll - offset, behavior: 'smooth' });
        }
        this.tempScroll = this.boundings.top + this.scroll - offset;
      }
    }
  };

  render() {
    /*
    if(!this.props.themes.themes.find(x => x.id == this.props.editTheme)) {
      return null
    }
    */

    const {
      title,
      ctaBtnProps,
      ctaBtnHide,
      ctaBtnText,
      ctaBtnAction,
      ctaBtnWrap,
      openBtnHide,
      openBtnText,
      cancelBtnHide,
      cancelBtnText,
      noticeShow,
      noticeType,
      noticeText,
      noticeAction,
      content,
      containsError,
      pending,
      initialOpen,
      pinSectionOnOpen,
      id
    } = this.props;

    const {
      showBackdrop,
    } = this.state;
    // initialOpen holds the section open: it cannot be closed while the prop is set
    const open = this.state.open || initialOpen;
    // an open section shows its bar inline under the header, never pinned: the layout shift
    // while opening flips the observer for a frame, and the bar would flash at the bottom
    const fixedNotice = this.state.fixedNotice && !open;

    return(
      <Root ref={this.setRootRef} key={id} id={id} className={classNames(classes.root, (pinSectionOnOpen && open) ? 'pinned' : null)}>
        <Fade in={showBackdrop} timeout={{enter: 1000}} unmountOnExit>
          <div className={classes.backdrop} onClick={this.handleClose} style={!showBackdrop ? {pointerEvents: 'none', display: 'none'} : {}}></div>
        </Fade>
        <Collapse in={noticeShow} onClick={!pending ? this.handleToggleOpen : null}>
          <div>
            {fixedNotice &&
              <div style={{height: this.noticeRef?.offsetHeight || 42.05, position: 'relative'}}></div>
            }
            <div ref={el => { this.noticeRef = el; }} className={classNames('sticky-notice', classes.notice, classes[noticeType], fixedNotice ? 'is-fixed' : null)} onClick={ fixedNotice ? this.handleClickNotice : null} style={{ cursor: fixedNotice ? 'pointer' : 'default', ...(fixedNotice ? this.getFixedNoticeStyle() : {})}}>
              <Toolbar className={classNames('toolbar')}>
                {/* icon, text, optional action (a help link, say) in one row - every section's
                    notice renders the same way, whatever text it passes */}
                <p style={{ display: 'flex', alignItems: 'center' }}>
                  {noticeType === 'error' ? <ErrorOutline className={classes.noticeIcon} style={{ flexShrink: 0 }} /> : <InfoOutlinedIcon className={classes.noticeIcon} style={{ flexShrink: 0 }} />}
                  <span style={{ flex: 1, minWidth: 0 }}>{noticeText}</span>
                  {noticeAction &&
                    <span style={{ marginLeft: 12, display: 'inline-flex', flexShrink: 0 }} onClick={e => e.stopPropagation()}>{noticeAction}</span>
                  }
                </p>
              </Toolbar>
            </div>
          </div>
        </Collapse>
        <div variant="body1" className={classNames('collapsable-section', classes.contentContainer, noticeShow && classes.noticed)}>
          <Paper className={classes.paper} elevation={(pinSectionOnOpen && open) ? 10 : 1}>
            <Toolbar
              className={classNames('toolbar', !initialOpen && classes.toolbarToggler)}
              onClick={!pending && !initialOpen ? this.handleToolbarClick : null}
            >
              <h3 className={classes.collapseToggler} style={initialOpen ? { cursor: 'default' } : undefined}>
                {/* a locked section shows no toggle, but keeps its place so the title lines up */}
                {open ? <RemoveOutlined className={classes.icon} style={initialOpen ? { visibility: 'hidden' } : undefined} /> : <AddIcon className={classes.icon} />}
                {title}
              </h3>
              {open ?
                <div className={classes.toolbarActions} style={{ position: 'absolute', right: 16 }} >
                  {!cancelBtnHide && !initialOpen &&
                    <Button
                      variant="outlined"
                      onClick={this.handleClose}
                      disabled={pending ? true : false}
                      className={classes.button}>
                      {cancelBtnText}
                    </Button>
                  }
                  {!ctaBtnHide && (ctaBtnWrap || (btn => btn))(
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={ctaBtnAction}
                      disabled={pending ? true : false}
                      className={classes.button}
                      {...ctaBtnProps}
                    >
                      {ctaBtnText}
                      {pending &&
                        <CircularProgress color="primary" size={16} style={{marginLeft: 10}} />
                      }
                    </Button>
                  )}
                </div>
              :
                <div className={classes.toolbarActions} style={{ position: 'absolute', right: 16 }} >
                  {!pending ?
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.handleToggleOpen}
                      className={classes.button}>
                      {openBtnText}
                    </Button>
                  :
                    <CircularProgress size={24} style={{marginRight: 10}} />
                  }
                </div>
              }
            </Toolbar>
            <Collapse
              onEnter={this.scrollToTop}
              onEntered={this.scrollToTop}
              onExited={this.handleClosed}
              //className={classNames('container')}
              in={open || initialOpen}
            >
              <Divider />
              {content}
            </Collapse>
          </Paper>
        </div>
      </Root>
    );
  }
}

CollapsableSection.propTypes = {
};

const mapStateToProps = (state) => ({
  themes: state.themes,
  editTheme: state.themes.edit_theme,
  master_loading: state.master_loading,
  progress: state.progress
})

export default withRouter(connect(
  mapStateToProps
)(CollapsableSection))
